<template>
	<div class="xs-12">
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("actions") }}</div>

		<!-- Body -->
		<j-card class="bg5 bg4--l3">
			<!-- Send Offer -->
			<div class="flex xs-12 xs-middle xs-between p2 bb1 bc7">
				<div class="xs-6 fs3 fw7 c6 lh125">
					{{ $t("send_offer_to_client") }}
				</div>
				<div class="flex xs-6 xs-right">
					<j-button
						size="xsmall"
						:isLoading="isSendOfferLoading"
						v-tooltip="sendOfferTooltip"
						@click="sendOffer"
						action="sendOffer"
						>{{ $t("send") }}</j-button
					>
				</div>
			</div>

			<!-- Reject -->
			<div class="flex xs-12 xs-middle xs-between p2 bb1 bc7">
				<div class="xs-6 fs3 fw7 c6 lh125">
					Reject Proposal
				</div>
				<div class="flex xs-6 xs-right">
					<j-button
						size="xsmall"
						:isDisabled="isRejected"
						:isLoading="isRejectLoading"
						@click="$modal.show('rejectProposalModal')"
						>{{ $t("reject") }}</j-button
					>
				</div>
			</div>
		</j-card>

		<!-- Reject Proposal Modal -->
		<core-modal name="rejectProposalModal" :headline="$t('reject')">
			<reject-proposal
				:payload="proposal"
				:id="proposalId"
				@startReject="isRejectLoading = true"
				@endReject="endRejection"
				@rejected="afterRejection"
			/>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import RejectProposal from "./RejectProposal"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

/**
 * This component is responsible for numerous actions that can happen on policy
 * It's a smart component that is calling APIs
 */
export default {
	components: { JCard, JButton, CoreModal, RejectProposal },

	data() {
		return {
			isRequestPaymentLoading: false,
			isIssueLoading: false,
			isIssueManuallyLoading: false,
			isSendOfferLoading: false,
			isconvertToQuoteLoading: false,
			isRejectLoading: false,
			form: {
				to: null
			},
			forceIssue: false,
			warnings: null,
			// For manual issuance
			issuerNumber: null
		}
	},

	computed: {
		sendOfferTooltip() {
			return {
				placement: "auto",
				content: this.$t("send_offer_description"),
				delay: { show: 500 }
			}
		},

		issueTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_description"),
				delay: { show: 100 }
			}
		},

		issueManuallyTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_manually_description"),
				delay: { show: 100 }
			}
		},

		env() {
			return this.$store.state.settings.env
		},
		proposalId() {
			return this.$store.state.caseManagement.proposalId
		},
		proposal() {
			return this.$store.state.caseManagement.proposals.filter(p => {
				return p.id === this.proposalId
			})[0]
		},
		caseId() {
			return this.$store.state.caseManagement.caseId
		},
		offer() {
			return this.$store.state.caseManagement.proposals.find(
				item => item.id === this.proposalId
			)
		},
		isRejected() {
			return (
				(this.proposal.status || "").toString().toLowerCase() === "rejected"
			)
		}
	},

	methods: {
		async sendOffer() {
			this.isSendOfferLoading = true

			const { caseManagement } = this.$store.state

			const toEmail = caseManagement.case?.holder?.contacts
				?.filter(item => item.type === "email")
				?.map(item => item.value)
				?.toString()

			if (!toEmail) {
				this.$store.dispatch("showError", this.$t("no_email"))
				return
			}

			const hasLoading = this.offer.basket[0]?.premium?.loadings?.length > 0
			const hasExclusion = this.proposal.basket[0]?.exclusions?.length > 0

			const plan = this.offer.basket[0]?.product?.productId?.plan

			const templates = {
				"S00027-01-000-02":
					hasLoading || hasExclusion
						? "EMAIL_VHIS_STANDARD_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_VHIS_STANDARD_COUNTEROFFER_CASE",
				"F00038-01-000-02":
					hasLoading || hasExclusion
						? "EMAIL_VHIS_FLEXI_SEMIPRIVATE_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_VHIS_FLEXI_SEMIPRIVATE_COUNTEROFFER_CASE",
				"F00038-02-000-02":
					hasLoading || hasExclusion
						? "EMAIL_VHIS_FLEXI_PRIVATE_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_VHIS_FLEXI_PRIVATE_COUNTEROFFER_CASE",
				"F00061-01-000-01":
					hasLoading || hasExclusion
						? "EMAIL_ICARE_PLAN_1_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_ICARE_PLAN_1_COUNTEROFFER_CASE",
				"F00061-01-001-01":
					hasLoading || hasExclusion
						? "EMAIL_ICARE_PLAN_1A_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_ICARE_PLAN_1A_COUNTEROFFER_CASE",
				"F00061-02-000-01":
					hasLoading || hasExclusion
						? "EMAIL_ICARE_PLAN_2_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_ICARE_PLAN_2_COUNTEROFFER_CASE",
				"F00061-02-001-01":
					hasLoading || hasExclusion
						? "EMAIL_ICARE_PLAN_2A_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_ICARE_PLAN_2A_COUNTEROFFER_CASE",
				"F00061-02-002-01":
					hasLoading || hasExclusion
						? "EMAIL_ICARE_PLAN_2B_COUNTEROFFER_CASE_HASENDORSEMENT"
						: "EMAIL_ICARE_PLAN_2B_COUNTEROFFER_CASE"
			}

			const query = gql`
				mutation sendNotification($input: sendNotificationInput!) {
					sendNotification(input: $input) {
						errors
						status
					}
				}
			`

			const res = await api({
				query,
				variables: {
					input: {
						type: "counteroffer",
						emailMessage: {
							from: this.env.VHIS_EMAIL_FROM,
							to: toEmail,
							templateRendering: {
								templateId: this.env[templates[plan]],
								input: {
									name: "data",
									variablesJsonString: JSON.stringify({
										caseWhere1: { id: this.caseId },
										proposalWhere1: { id: this.proposalId }
									})
								}
							}
						}
					}
				}
			})

			this.isSendOfferLoading = false

			// Errros
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$store.dispatch("showSuccess", this.$t("email_was_sent"))
		},

		async convertToQuote(offerId) {
			this.isconvertToQuoteLoading = true

			const res = await api.convertOffer({
				policyId: this.policyId,
				offerId
			})

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				this.isconvertToQuoteLoading = false // remove loading when error
				return
			}

			// Success
			this.$store.dispatch(
				"showSuccess",
				this.$t("offer_was_converted_to_quote")
			)
			this.isconvertToQuoteLoading = false
		},

		endRejection() {
			this.isRejectLoading = false
			this.$modal.hide("rejectProposalModal")
		},

		afterRejection() {
			this.$store.dispatch("caseManagement/fetchProposals", {
				caseId: this.$store.state.caseManagement.caseId
			})
		}
	}
}
</script>
